// Amplification / pre-out assignment — README.md "AVR-03 and AVR-04:
// amplification and pre-outs". This is pure bookkeeping: it decides, for
// every passive speaker, whether it is driven by the AV unit's internal
// amplifier or must move to an external power amp (and which one, which
// channel), and for every active speaker whether its group has a working
// pre-out. AVR-03/AVR-04 issue text lives in rules/avrAmp.ts; IMP-01,
// PWR-02 and the Connect checklist items all read this same result so the
// "who drives what" answer is computed exactly once.

import type { EditorDoc, GearEntry, SpeakerEntry } from '@roomtreatment/core';
import type { AvSpecs, PowerAmpSpecs, Product } from '@roomtreatment/av-catalog';
import type { Channel, ChannelGroup } from './types';
import { channelGroup } from './channels';

export type DrivingAmp =
  | { kind: 'internal' }
  | { kind: 'external'; gearId: string; channelNumber: number }
  /** Active (self-powered) speaker fed line-level from the AV unit's
   *  pre-out — never an amplifier channel. */
  | { kind: 'preout' };

export interface SpeakerAssignment {
  speakerId: string;
  channel: Channel;
  group: ChannelGroup;
  active: boolean;
  product: Product | undefined;
  /** null: no working signal path was found (AVR-03 or AVR-04 fired for it). */
  driving: DrivingAmp | null;
}

export interface AmpGear {
  gearId: string;
  product: Product;
  specs: PowerAmpSpecs;
}

export interface AssignmentResult {
  bySpeaker: Map<string, SpeakerAssignment>;
  /** Groups moved out to an external amp successfully (or awaiting one —
   *  i.e. not blocked by a missing pre-out). Empty when nothing left the
   *  AV unit. Used for the `setup:amp-assign` checklist item. */
  externalGroups: ChannelGroup[];
  /** AVR-03: passive speakers whose group has a pre-out but no amplifier
   *  channel was available for them. */
  noAmpChannel: SpeakerAssignment[];
  /** AVR-04: speakers (passive-moved-out or active) whose group has no
   *  pre-out at all, keyed by group. */
  noPreout: Map<ChannelGroup, SpeakerAssignment[]>;
}

/** Priority order channels leave the AV unit in — README "Channels move out
 *  in this order, one pair at a time where the group is a pair". */
const MOVE_ORDER: ChannelGroup[] = [
  'height2', 'height1', 'front', 'center', 'surround_back', 'surround',
];

/** Deterministic intra-group channel order, independent of `doc.speakers`
 *  array order — required for stable ids under speaker reorder. */
const CHANNEL_ORDER: Record<ChannelGroup, Channel[]> = {
  front: ['FL', 'FR'],
  center: ['C'],
  surround: ['SL', 'SR'],
  surround_back: ['SBL', 'SBR'],
  height1: ['TFL', 'TFR', 'TML', 'TMR'],
  height2: ['TRL', 'TRR'],
  // No v1 layout uses a third height or front-wide pair; the group exists so units that have one are described fully.
  height3: [],
  height4: [],
  wide: [],
};

export function isActiveProduct(product: Product | undefined): boolean {
  return !!product && product.specs.kind === 'speaker' && product.specs.active.v === true;
}

export function assignAmplification(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  channels: Record<string, Channel>,
  bedIds: string[],
  overheadIds: string[],
  avUnit: { entry: GearEntry; product: Product; specs: AvSpecs },
  amps: AmpGear[],
): AssignmentResult {
  const speakersById = new Map<string, SpeakerEntry>(doc.speakers.map((s) => [s.id, s]));
  const channelled = [...bedIds, ...overheadIds];

  type Member = { speakerId: string; channel: Channel; product: Product | undefined; active: boolean };
  const byGroup = new Map<ChannelGroup, Member[]>();
  for (const id of channelled) {
    const channel = channels[id];
    const group = channelGroup(channel);
    if (!group) continue; // LFE never reaches here anyway
    const entry = speakersById.get(id);
    if (!entry) continue;
    const product = entry.productId ? catalog.get(entry.productId) : undefined;
    const active = isActiveProduct(product);
    const list = byGroup.get(group) ?? [];
    list.push({ speakerId: id, channel, product, active });
    byGroup.set(group, list);
  }
  // Deterministic ordering within each group.
  for (const [group, list] of byGroup) {
    const order = CHANNEL_ORDER[group];
    list.sort((a, b) => order.indexOf(a.channel) - order.indexOf(b.channel));
  }

  const passiveCountByGroup = new Map<ChannelGroup, number>();
  let totalPassive = 0;
  for (const [group, list] of byGroup) {
    const n = list.filter((m) => !m.active).length;
    passiveCountByGroup.set(group, n);
    totalPassive += n;
  }

  const internalCapacity = avUnit.specs.ampChannels.v;
  const groupsToMoveOut: ChannelGroup[] = [];
  if (totalPassive > internalCapacity) {
    let overflow = totalPassive - internalCapacity;
    let moved = 0;
    for (const group of MOVE_ORDER) {
      if (moved >= overflow) break;
      const n = passiveCountByGroup.get(group) ?? 0;
      if (n === 0) continue;
      groupsToMoveOut.push(group);
      moved += n;
    }
  }
  const moveOutSet = new Set(groupsToMoveOut);

  const preoutGroups = new Set(avUnit.specs.preouts.v.groups);
  const bySpeaker = new Map<string, SpeakerAssignment>();
  const noPreout = new Map<ChannelGroup, SpeakerAssignment[]>();
  const externalPool: { speakerId: string; channel: Channel; group: ChannelGroup; product: Product | undefined }[] = [];
  const externalGroupsSet = new Set<ChannelGroup>();

  for (const [group, list] of byGroup) {
    const groupMovesOut = moveOutSet.has(group);
    const hasPreout = preoutGroups.has(group);
    for (const m of list) {
      const needsPreout = m.active || (!m.active && groupMovesOut);
      if (needsPreout && !hasPreout) {
        const assignment: SpeakerAssignment = {
          speakerId: m.speakerId, channel: m.channel, group, active: m.active,
          product: m.product, driving: null,
        };
        bySpeaker.set(m.speakerId, assignment);
        const list2 = noPreout.get(group) ?? [];
        list2.push(assignment);
        noPreout.set(group, list2);
        continue;
      }
      if (m.active) {
        bySpeaker.set(m.speakerId, {
          speakerId: m.speakerId, channel: m.channel, group, active: true,
          product: m.product, driving: { kind: 'preout' },
        });
        continue;
      }
      if (!groupMovesOut) {
        bySpeaker.set(m.speakerId, {
          speakerId: m.speakerId, channel: m.channel, group, active: false,
          product: m.product, driving: { kind: 'internal' },
        });
        continue;
      }
      // Passive, moving out, group has a pre-out: queue for amp assignment.
      externalPool.push({ speakerId: m.speakerId, channel: m.channel, group, product: m.product });
      externalGroupsSet.add(group);
    }
  }

  // Order the external pool deterministically by group priority, then
  // intra-group channel order (both independent of array order).
  externalPool.sort((a, b) => {
    const gd = MOVE_ORDER.indexOf(a.group) - MOVE_ORDER.indexOf(b.group);
    if (gd !== 0) return gd;
    return CHANNEL_ORDER[a.group].indexOf(a.channel) - CHANNEL_ORDER[a.group].indexOf(b.channel);
  });

  const noAmpChannel: SpeakerAssignment[] = [];
  let poolIdx = 0;
  for (const amp of amps) {
    for (let ch = 1; ch <= amp.specs.channels.v && poolIdx < externalPool.length; ch++, poolIdx++) {
      const m = externalPool[poolIdx];
      bySpeaker.set(m.speakerId, {
        speakerId: m.speakerId, channel: m.channel, group: m.group, active: false,
        product: m.product, driving: { kind: 'external', gearId: amp.gearId, channelNumber: ch },
      });
    }
  }
  for (; poolIdx < externalPool.length; poolIdx++) {
    const m = externalPool[poolIdx];
    const assignment: SpeakerAssignment = {
      speakerId: m.speakerId, channel: m.channel, group: m.group, active: false,
      product: m.product, driving: null,
    };
    bySpeaker.set(m.speakerId, assignment);
    noAmpChannel.push(assignment);
  }

  return {
    bySpeaker,
    externalGroups: MOVE_ORDER.filter((g) => externalGroupsSet.has(g)),
    noAmpChannel,
    noPreout,
  };
}
